<script setup>
import Swiper from 'swiper'
import { EffectFade, Pagination, Autoplay, Parallax, Keyboard } from 'swiper/modules'

const props = defineProps({
    banner: {
        type: Array,
        required: true,
        default: () => [],
    },
    autoplayDelay: {
        type: Number,
        default: 5000,
        validator: value => value >= 1000
    },
    transitionSpeed: {
        type: Number,
        default: 800,
        validator: value => value >= 300 && value <= 2000
    },
    preloadDistance: {
        type: Number,
        default: 2,
        validator: value => value >= 1 && value <= 5
    },
    buttontitle: {
        type: String,
        default: '查看详情'
    }
})

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'

const contentRef = ref(null)
const loading = ref(true)
const swiperInstance = ref(null)
const isSwiperInitializing = ref(false)
const activeSlideIndex = ref(0)
const isDocumentVisible = ref(isBrowser ? !document.hidden : true)
const isComponentMounted = ref(false)
const isHoverPaused = ref(false)

const loadedItems = ref(new Map())
const mediaCache = ref(new Map())
const slideDurationMap = ref(new Map())

const resizeTimer = ref(null)
const preloadTimer = ref(null)

const visibilityListener = ref(null)
const resizeObserver = ref(null)
const intersectionObserver = ref(null)

const hasSingleBanner = computed(() => props.banner.length <= 1)

const isImage = url => !!url && /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url)

const getMediaCacheKey = item => {
    try {
        return btoa(encodeURIComponent(item.img || '')).substring(0, 16)
    } catch {
        return String(item.img || '').substring(0, 16)
    }
}

const optimizedBanner = computed(() => {
    return props.banner.map((item, index) => ({
        ...item,
        uniqueKey: `${item.id}_${index}_${getMediaCacheKey(item)}`,
        isLoaded: loadedItems.value.has(item.id)
    }))
})

const getVideoCacheKey = video => video?.src || video?.querySelector('source')?.src || ''

const getVideoType = url => {
    if (/\.webm($|\?)/i.test(url)) return 'video/webm'
    if (/\.ogg($|\?)/i.test(url)) return 'video/ogg'
    return 'video/mp4'
}

const setSlideDuration = (index, duration) => {
    slideDurationMap.value.set(index, duration || props.autoplayDelay)
}

const preloadVideoDuration = item => {
    return new Promise(resolve => {
        const video = document.createElement('video')

        video.preload = 'metadata'
        video.muted = true
        video.src = item.img

        const done = duration => {
            video.onloadedmetadata = null
            video.onerror = null
            video.removeAttribute('src')
            video.load()
            resolve(duration)
        }

        video.onloadedmetadata = () => {
            const duration = video.duration && !isNaN(video.duration)
                ? Math.max(video.duration * 1000, 1000)
                : props.autoplayDelay

            done(duration)
        }

        video.onerror = () => done(props.autoplayDelay)
    })
}

const preloadAllSlideDurations = async () => {
    slideDurationMap.value.clear()

    await Promise.all(
        props.banner.map(async (item, index) => {
            if (isImage(item.img)) {
                setSlideDuration(index, props.autoplayDelay)
                return
            }

            const duration = await preloadVideoDuration(item)
            setSlideDuration(index, duration)
        })
    )
}

const getRealIndex = () => {
    const swiper = swiperInstance.value
    return swiper?.realIndex ?? swiper?.activeIndex ?? 0
}

const getSlideDuration = () => {
    return slideDurationMap.value.get(getRealIndex()) || props.autoplayDelay
}

const syncSlideDuration = () => {
    const swiper = swiperInstance.value
    if (!swiper || isHoverPaused.value || hasSingleBanner.value || !swiper.autoplay) return

    swiper.params.autoplay.delay = getSlideDuration()
    swiper.autoplay.stop()
    swiper.autoplay.start()
}

const pausePaginationAnimation = () => {
    contentRef.value?.classList.add('is-hover-paused')
}

const resumePaginationAnimation = () => {
    contentRef.value?.classList.remove('is-hover-paused')
}

const getActiveSlide = () => {
    const swiper = swiperInstance.value
    return swiper?.slides?.[swiper.activeIndex]
}

const getSlideMedia = slide => {
    if (!slide) return {}
    return {
        video: slide.querySelector('video'),
        img: slide.querySelector('img')
    }
}

const handleMouseEnter = () => {
    isHoverPaused.value = true
    swiperInstance.value?.autoplay?.stop()
    pauseAllVideos()
    pausePaginationAnimation()
}

const handleMouseLeave = () => {
    isHoverPaused.value = false
    resumePaginationAnimation()
    playMediaInSlide(getActiveSlide())

    if (!hasSingleBanner.value) {
        syncSlideDuration()
    }
}

const getSwiperOptions = () => {
    const firstDuration = slideDurationMap.value.get(0) || props.autoplayDelay

    return {
        direction: 'horizontal',
        effect: 'fade',
        fadeEffect: { crossFade: true },
        autoplay: hasSingleBanner.value ? false : {
            delay: firstDuration,
            disableOnInteraction: false,
            waitForTransition: true,
            pauseOnMouseEnter: false
        },
        parallax: true,
        speed: props.transitionSpeed,
        loop: !hasSingleBanner.value,
        loopAdditionalSlides: 0,
        loopedSlides: Math.min(props.banner.length, 2),
        keyboard: { enabled: true, onlyInViewport: true },
        pagination: {
            el: '.swiper-pagination',
            type: 'bullets',
            clickable: true,
            renderBullet: (index, className) => {
                const duration = slideDurationMap.value.get(index) || props.autoplayDelay
                return `<span class="${className}" style="--bullet-progress-duration:${duration}ms"></span>`
            }
        },
        preloadImages: false,
        lazy: {
            loadPrevNext: true,
            loadPrevNextAmount: props.preloadDistance,
            loadOnTransitionStart: true,
            checkInView: true
        },
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        modules: [EffectFade, Pagination, Autoplay, Parallax, Keyboard],
        on: {
            init: handleSwiperInit,
            slideChangeTransitionStart: handleSlideChangeStart,
            slideChangeTransitionEnd: handleSlideChangeEnd,
            touchStart: handleTouchStart,
            touchEnd: handleTouchEnd,
            resize: handleResize
        }
    }
}

const initSwiper = () => {
    if (!props.banner.length || swiperInstance.value || isSwiperInitializing.value || !contentRef.value) return

    isSwiperInitializing.value = true

    nextTick(() => {
        try {
            swiperInstance.value = new Swiper(contentRef.value, getSwiperOptions())
            fixLoopBehavior()

            const activeSlide = getActiveSlide()
            if (activeSlide) {
                playMediaInSlide(activeSlide)
                activeSlideIndex.value = getRealIndex()
                syncSlideDuration()
            }

            swiperInstance.value.on('slideChange', () => {
                activeSlideIndex.value = getRealIndex()
            })
        } catch (error) {
            console.error('Swiper initialization error:', error)
        } finally {
            isSwiperInitializing.value = false
        }
    })
}

const fixLoopBehavior = () => {
    const swiper = swiperInstance.value
    if (!swiper || hasSingleBanner.value) return

    try {
        swiper.loopCreate()
        swiper.update()
    } catch (error) {
        console.error('Loop fix error:', error)
    }
}

const handleSwiperInit = () => {
    playMediaInSlide(getActiveSlide())
    syncSlideDuration()
}

const handleSlideChangeStart = () => {
    pauseAllVideos()
}

const handleSlideChangeEnd = () => {
    playMediaInSlide(getActiveSlide())
    resetInactiveVideos()
    syncSlideDuration()
}

const handleTouchStart = () => {
    swiperInstance.value?.autoplay?.stop()
}

const handleTouchEnd = () => {
    const swiper = swiperInstance.value
    if (isComponentMounted.value && swiper && !swiper.destroyed && swiper.autoplay && !isHoverPaused.value) {
        syncSlideDuration()
    }
}

const handleResize = () => {
    swiperInstance.value?.update()
}

const playMediaInSlide = slide => {
    if (!slide || !isDocumentVisible.value || isHoverPaused.value) return

    const { video, img } = getSlideMedia(slide)

    if (img && !img.complete) {
        img.loading = 'eager'
    }

    if (video) {
        playVideo(video)
    }
}

const playVideo = video => {
    if (!video || isHoverPaused.value) return

    try {
        const cacheKey = getVideoCacheKey(video)

        if (!mediaCache.value.has(cacheKey)) {
            video.setAttribute('preload', 'auto')
            mediaCache.value.set(cacheKey, true)
        }

        video.muted = true

        const playPromise = video.play()
        if (playPromise) {
            playPromise.catch(error => {
                console.log('Video play failed:', error)
                retryVideoPlay(video, 2)
            })
        }
    } catch (error) {
        console.error('Video play error:', error)
    }
}

const retryVideoPlay = (video, retriesLeft) => {
    if (retriesLeft <= 0 || !isComponentMounted.value || isHoverPaused.value) return

    setTimeout(() => {
        if (!isComponentMounted.value || isHoverPaused.value) return

        video.play().catch(() => {
            retryVideoPlay(video, retriesLeft - 1)
        })
    }, 300)
}

const pauseAllVideos = () => {
    swiperInstance.value?.slides?.forEach(slide => {
        slide.querySelector('video')?.pause()
    })
}

const resetInactiveVideos = () => {
    const swiper = swiperInstance.value
    if (!swiper?.slides) return

    swiper.slides.forEach((slide, index) => {
        if (index === swiper.activeIndex) return

        const video = slide.querySelector('video')
        if (video) {
            video.pause()
            video.currentTime = 0
        }
    })
}

const debouncedPreload = centerIndex => {
    clearTimeout(preloadTimer.value)
    preloadTimer.value = setTimeout(() => {
        preloadAdjacentItems(centerIndex)
    }, 50)
}

const preloadAdjacentItems = centerIndex => {
    if (!props.banner.length || !isComponentMounted.value) return

    const start = Math.max(0, centerIndex - props.preloadDistance)
    const end = Math.min(props.banner.length - 1, centerIndex + props.preloadDistance)

    for (let i = start; i <= end; i++) {
        const item = props.banner[i]
        if (item && !loadedItems.value.has(item.id)) {
            const slide = swiperInstance.value?.slides?.[i]
            if (slide) preloadMediaInSlide(slide)
        }
    }
}

const preloadMediaInSlide = slide => {
    const { video, img } = getSlideMedia(slide)

    if (video) {
        const key = getVideoCacheKey(video)
        if (!mediaCache.value.has(key)) video.load()
        return
    }

    if (img && !img.complete && !mediaCache.value.has(img.src)) {
        img.loading = 'eager'
        mediaCache.value.set(img.src, true)
    }
}

const destroySwiper = () => {
    if (!swiperInstance.value) return

    try {
        swiperInstance.value.destroy(true, true)
    } catch {
    } finally {
        swiperInstance.value = null
    }
}

const handleBannerUpdate = async () => {
    destroySwiper()
    await preloadAllSlideDurations()
    initSwiper()
    debouncedPreload(0)
}

const setupVisibilityHandler = () => {
    if (!isBrowser) return

    visibilityListener.value = () => {
        isDocumentVisible.value = !document.hidden

        const swiper = swiperInstance.value
        if (!swiper) return

        if (document.hidden) {
            swiper.autoplay?.stop()
            pauseAllVideos()
            pausePaginationAnimation()
            return
        }

        if (!isHoverPaused.value) {
            resumePaginationAnimation()
            playMediaInSlide(getActiveSlide())
            syncSlideDuration()
        }
    }

    document.addEventListener('visibilitychange', visibilityListener.value, { passive: true })
}

const setupResizeObserver = () => {
    if (!isBrowser || !contentRef.value || !window.ResizeObserver) return

    resizeObserver.value = new ResizeObserver(entries => {
        if (!entries.length || !isComponentMounted.value) return

        clearTimeout(resizeTimer.value)
        resizeTimer.value = setTimeout(() => {
            swiperInstance.value?.update()
        }, 100)
    })

    resizeObserver.value.observe(contentRef.value)
}

const setupIntersectionObserver = () => {
    if (!isBrowser || !window.IntersectionObserver) return

    intersectionObserver.value = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                handleMediaIntersection(entry.target)
            }
        })
    }, {
        root: contentRef.value,
        rootMargin: `${props.preloadDistance * 100}px`,
        threshold: 0.01
    })

    nextTick(() => {
        if (!isComponentMounted.value) return

        contentRef.value
            ?.querySelectorAll('img, video')
            ?.forEach(el => intersectionObserver.value.observe(el))
    })
}

const handleMediaIntersection = media => {
    if (media.tagName === 'VIDEO' && media.readyState === 0) {
        media.load()
    }

    markMediaLoaded(media)
}

const markMediaLoaded = media => {
    const slideIndex = Number(media.dataset.index)
    if (Number.isNaN(slideIndex)) return

    const item = props.banner[slideIndex]
    if (!item) return

    loadedItems.value.set(item.id, true)

    if (media.tagName === 'VIDEO') {
        mediaCache.value.set(getVideoCacheKey(media), true)
    } else if (media.src) {
        mediaCache.value.set(media.src, true)
    }
}

const handleImageLoad = event => {
    markMediaLoaded(event.target)
}

const handleVideoLoaded = event => {
    const video = event.target

    markMediaLoaded(video)

    const activeSlide = getActiveSlide()
    if (activeSlide?.contains(video) && isDocumentVisible.value && !isHoverPaused.value) {
        playVideo(video)
    }
}

const hideLoading = () => {
    loading.value = false
}

const cleanup = () => {
    isComponentMounted.value = false

    clearTimeout(resizeTimer.value)
    clearTimeout(preloadTimer.value)

    if (isBrowser && visibilityListener.value) {
        document.removeEventListener('visibilitychange', visibilityListener.value)
    }

    resizeObserver.value?.disconnect()
    intersectionObserver.value?.disconnect()

    pauseAllVideos()
    destroySwiper()

    hideLoading()
    loadedItems.value.clear()
    mediaCache.value.clear()
    slideDurationMap.value.clear()
}

watch(() => props.banner, newVal => {
    if (!newVal.length) return

    hideLoading()
    nextTick(handleBannerUpdate)
}, { immediate: true, deep: true })

watch(() => props.autoplayDelay, async () => {
    await preloadAllSlideDurations()

    const swiper = swiperInstance.value
    if (swiper) {
        swiper.pagination?.render()
        swiper.pagination?.update()
        syncSlideDuration()
    }
})

watch(activeSlideIndex, debouncedPreload)

onMounted(() => {
    isComponentMounted.value = true
    setupVisibilityHandler()
    setupResizeObserver()
    setupIntersectionObserver()
})

onBeforeUnmount(cleanup)
</script>

<template>
    <div
        class="Heziyunswiper swiper swiper-container-h creative-fullpage--slider banner-horizental"
        ref="contentRef"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="swiper-wrapper">
            <div
                class="swiper-slide"
                v-for="(item, index) in optimizedBanner"
                :key="item.uniqueKey"
                :data-index="index"
            >
                <a-spin :loading="loading" tip="This may take a while...">
                    <div class="slider-inner" data-swiper-parallax="100">
                        <a-image
                            v-if="isImage(item.img)"
                            :src="item.img"
                            show-loader
                            :preview="false"
                            :data-index="index"
                            @load="handleImageLoad"
                        />

                        <video
                            v-else
                            preload="metadata"
                            muted
                            playsinline
                            webkit-playsinline
                            x5-playsinline
                            x5-video-player-type="h5"
                            x5-video-player-fullscreen="true"
                            x5-video-orientation="portraint"
                            disablePictureInPicture
                            :poster="item.poster || ''"
                            :data-index="index"
                            @loadedmetadata="handleVideoLoaded"
                            :type="getVideoType(item.img)"
                        >
                            <source :src="item.img" :type="getVideoType(item.img)" />
                        </video>

                        <div class="swiper-content" data-swiper-parallax="2000">
                            <div class="Hui-label" v-if="item.small" v-html="item.small"></div>
                            <div class="title-area">
                                <span class="title" v-html="item.title"></span>
                            </div>
                            <p class="disc" v-html="item.details"></p>
                            <div class="creative-btn--wrap">
                                <a class="creative-slide--btn Hui-lightSpeed" role="button" :href="item.url">
                                    <div class="creative-btn--label">
                                        <div class="creative-btn__text">{{ $t('index.bannerButton') }}</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </a-spin>
            </div>
        </div>
        <div class="swiper-pagination"></div>
    </div>
</template>

<style>
.banner-horizental .arco-image {
    height: 100%;
    width: 100%;
}
</style>

<style lang="less">
@font-face {
    font-family: "italices";
    font-weight: 400;
    src: url("@/static/font/file/italics.woff2") format("woff2");
    font-display: swap;
}

.Heziyunswiper {
    height: 100%;
    position: relative;

    @media (max-width: 768px) {
        height: 75vw !important;
    }
}

.creative-fullpage--slider {
    background-color: #ffffff;
    z-index: 2;
    width: 100%;
    position: relative;
    flex-direction: column;
    display: flex;
    clip-path: none !important;

    @media (max-width: 768px) {
        margin-top: 0;
    }

    .slider-inner {
        background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 37%, #f2f2f2 63%);
        background-size: 400% 100%;
        animation: skeleton-loading 1s ease infinite;
        -webkit-animation: skeleton-loading 1s ease infinite;
        position: relative;
        height: 100%;

        img,
        video {
            object-fit: cover;
            width: 100%;
            height: 100%;
            background-color: #fff;
        }

        .swiper-content {
            width: 80rem;
            position: absolute;
            top: 50%;
            left: 50%;
            z-index: 1;
            transform: translate(-50%, -50%) !important;

            @media(max-width: 991px) {
                width: 100%;
                text-align: center;
                left: 0;
                transform: translate(0, -50%) !important;
            }

            .Hui-label {
                color: #fff;
                width: fit-content;
                margin-left: 0.5rem;
                margin-bottom: 0.5rem;
                display: none;
                overflow: hidden;

                @media (max-width: 768px) {
                    padding: 0.5vw 4vw;
                    margin: 2vw auto;
                    margin-bottom: 2vw;
                    font-size: 3.5vw;
                }
            }

            .title-area {
                .tag {
                    color: #ffffff;
                    font-weight: 900;
                    font-size: 1.3rem;
                    margin-bottom: 0.5rem;
                    margin-top: 0;

                    @media (max-width: 768px) {
                        display: none;
                    }
                }

                .title {
                    color: #181818;
                    font-size: 2.7rem;
                    font-family: "italices";
                    font-weight: 500;
                    line-height: 1.1;
                    text-transform: uppercase;
                    text-decoration: none;
                    display: none;

                    @media (max-width: 768px) {
                        color: #000;
                        font-size: 7vw;
                        padding: 0 5vw;
                        font-weight: 900;
                        display: block;
                    }
                }
            }

            p.disc {
                font-size: 0.8rem;
                margin: 1rem 0;
                font-weight: 400;
                line-height: 1.7;
                color: #181818;
                width: 30rem;
                display: none;
                font-family: "思源黑";

                @media (max-width: 768px) {
                    padding: 0 5vw;
                    margin: 3vw 0 0;
                    line-height: 7vw;
                    font-size: 4vw;
                    width: 100%;
                    display: -webkit-box !important;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    word-break: break-word;

                    br {
                        display: none;
                    }
                }

                .highlight {
                    color: #007aff;
                }
            }
        }
    }

    .swiper-slide {
        position: relative;
        display: flex;
        justify-content: center;
        text-align: left;
        flex-direction: column;
        overflow: hidden;
    }
}

.banner-horizental {
    height: 35rem;

    @media (max-width: 768px) {
        height: auto;
        padding-top: 15vw;

        .banner-horizental {
            padding-top: 0;
        }
    }
}

.swiper-pagination-fraction,
.swiper-pagination-custom,
.swiper-horizontal > .swiper-pagination-bullets,
.swiper-pagination-bullets.swiper-pagination-horizontal {
    position: absolute;
    bottom: 8rem !important;
    left: 50% !important;
    width: 80rem !important;
    display: flex;
    gap: 8px;
    transform: translateX(-50%);

    @media (max-width: 768px) {
        bottom: 5vw !important;
        margin: 0;
        justify-content: center;
        width: auto;
    }
}

.swiper-pagination-bullet {
    cursor: pointer;
    position: relative;
    overflow: hidden;
    background-color: rgba(38, 38, 38, .15) !important;
    opacity: 1 !important;
    width: 40px !important;
    height: 4px !important;
    border-radius: 0 !important;

    @media (max-width: 768px) {
        width: 9vw;
        height: 0.7vw;
    }
}

.swiper-pagination-bullet-active {
    position: relative;
}

.swiper-pagination-bullet-active::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: #007aff;
    animation: moving var(--bullet-progress-duration, 5000ms) linear forwards;
    -webkit-animation: moving var(--bullet-progress-duration, 5000ms) linear forwards;
}

.banner-horizental.is-hover-paused .swiper-pagination-bullet-active::before {
    animation-play-state: paused !important;
    -webkit-animation-play-state: paused !important;
}

.swiper-slide-active {
    .slider-inner {
        .swiper-content {
            .Hui-label {
                display: block;
                animation: left-right 0.5s cubic-bezier(0.49, 0, 0.01, 1);
            }

            .title-area .title {
                display: block;
                animation: left-right 0.8s cubic-bezier(0.49, 0, 0.01, 1);
                -webkit-animation: left-right 0.8s cubic-bezier(0.49, 0, 0.01, 1);
            }

            p.disc {
                display: block;
                animation: left-right 1.1s cubic-bezier(0.49, 0, 0.01, 1);
                -webkit-animation: left-right 1.1s cubic-bezier(0.49, 0, 0.01, 1);
            }
        }
    }

    .creative-btn--wrap .creative-slide--btn {
        display: inline-flex;
        animation: left-right 1.4s cubic-bezier(0.49, 0, 0.01, 1);
        -webkit-animation: left-right 1.4s cubic-bezier(0.49, 0, 0.01, 1);
    }
}

.creative-btn--wrap {
    .creative-slide--btn {
        color: #ffffff;
        font-size: 14px;
        padding: 0.7rem 2.5rem;
        background-color: #0052d9;
        font-weight: 400;
        display: none;
        position: relative;
        white-space: nowrap;
        text-decoration: none;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        user-select: none;
        outline: none;
        outline-color: transparent;
        box-shadow: 0 12px 40px rgba(11, 83, 255, 0.24);
        will-change: transform;
        backface-visibility: hidden;
        transition: all 0.3s ease;
        font-family: "思源黑";

        @media (max-width: 768px) {
            font-size: 4vw;
            margin-top: 3vw;
            padding: 1.5vw 4vw;
        }

        &:hover {
            opacity: 0.8;
        }
    }
}

@keyframes moving {
    from {
        left: -100%;
    }

    to {
        left: 0;
    }
}

@keyframes left-right {
    from {
        transform: translateX(-20px);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>